import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Trophy, X, Crown, Sparkles, Gift, Lock } from 'lucide-react';

const SESSION_KEY_PREFIX = 'jsf_top_seller_seen_';
const API = `${window.location.origin}/api`;

const DEFAULT_PERKS = [
  '1 FREE 30ml bottle (scent of choice)',
  'Cash bonus (sent via GCash)',
  'Spotlight feature post (Official Page)',
  'Official certificate (downloadable)',
];

/**
 * Celebratory popup for the current month's Top Seller.
 * Only shows once per session (sessionStorage flag scoped by month+ambassador).
 * Perks pulled live from /business-config/public so admin can edit them.
 */
export default function TopSellerPopup({ data, ambassadorSlug }) {
  const [open, setOpen] = useState(false);
  const [perks, setPerks] = useState(DEFAULT_PERKS);

  const monthLabel = data?.month_label;
  const top = data?.top;
  const sessionKey = `${SESSION_KEY_PREFIX}${data?.year}_${data?.month}_${ambassadorSlug || ''}`;

  useEffect(() => {
    if (!top || !monthLabel) return;
    let alreadySeen = false;
    try { alreadySeen = sessionStorage.getItem(sessionKey) === '1'; } catch { /* sessionStorage unavailable */ }
    if (alreadySeen) return;
    // Pull live perks
    axios.get(`${API}/business-config/public`).then(r => {
      if (r.data?.monthly_top_seller_perks?.length) setPerks(r.data.monthly_top_seller_perks);
    }).catch(() => {});
    const t = setTimeout(() => setOpen(true), 800);
    return () => clearTimeout(t);
  }, [top, monthLabel, sessionKey]);

  const close = () => {
    try { sessionStorage.setItem(sessionKey, '1'); } catch { /* sessionStorage unavailable */ }
    setOpen(false);
  };

  if (!open || !top) return null;

  // Same gating as the certificate page: claims open the moment the
  // tracked month ends. If the popup ever fires mid-month (e.g. the user
  // is currently #1 in the current month), we still celebrate them — but
  // the green "Claim Prize" button gets replaced with a locked notice.
  const monthIsOver = (() => {
    const now = new Date();
    if (data.year < now.getFullYear()) return true;
    if (data.year > now.getFullYear()) return false;
    return data.month < now.getMonth() + 1;
  })();

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4 animate-fade-in-up" data-testid="top-seller-popup">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={close} aria-hidden="true" />
      <div className="relative max-w-md w-full bg-[#0F0F0F] border-2 border-[#D4AF37] p-8 text-center shadow-[0_0_60px_rgba(212,175,55,0.25)]">
        {/* Close */}
        <button onClick={close} className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center text-white/40 hover:text-white" aria-label="Close" data-testid="top-seller-close">
          <X className="w-4 h-4" />
        </button>

        {/* Sparkles bg */}
        <Sparkles className="absolute top-6 left-6 w-4 h-4 text-[#D4AF37]/40 animate-pulse" />
        <Sparkles className="absolute bottom-8 right-10 w-3 h-3 text-[#D4AF37]/40 animate-pulse" style={{ animationDelay: '0.6s' }} />

        <Trophy className="w-14 h-14 text-[#D4AF37] mx-auto mb-4" strokeWidth={1.3} />
        <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3 font-sans">Top Seller — {monthLabel}</p>
        <h2 className="text-3xl text-white font-light leading-tight mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          You&apos;re the <span className="text-[#D4AF37] italic">#1 Seller</span> this month!
        </h2>
        <p className="text-white/60 text-sm font-sans mb-6">
          {top.bottles} bottles · ₱{Number(top.revenue).toLocaleString()} in sales. Sobrang ganda. 🔥
        </p>

        <div className="bg-[#141414] border border-white/10 p-4 mb-6 text-left">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-sans mb-2">Your rewards</p>
          <ul className="text-xs text-white/70 font-sans space-y-1.5">
            {perks.map((p, i) => <li key={i}>✓ {p}</li>)}
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          {monthIsOver ? (
            <button
              type="button"
              onClick={async () => {
                const token = localStorage.getItem('auth_token');
                if (!token) {
                  toast.error('Sign in to claim your prize.');
                  return;
                }
                const note = window.prompt(
                  "🎁 Claim your Top Seller prize!\n\n" +
                  "We'll send: cash bonus via GCash + 1 free 50ml bottle + spotlight feature.\n\n" +
                  "Add a quick note (scent you want, GCash number, or special request):",
                  ''
                );
                if (note === null) return;
                try {
                  await axios.post(`${API}/ambassador/me/claim-prize`, {
                    year: data.year, month: data.month, note: (note || '').slice(0, 500),
                  }, { headers: { Authorization: `Bearer ${token}` } });
                  toast.success('🎉 Prize claim submitted! Team will message you within 1-2 business days.');
                  close();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Could not submit claim.');
                }
              }}
              className="flex-1 px-4 py-3 bg-[#1C7D3F] text-white text-xs uppercase tracking-[0.2em] font-sans hover:bg-[#2E9B50] transition-colors flex items-center justify-center gap-2"
              data-testid="top-seller-claim-prize-btn"
            >
              <Gift className="w-3.5 h-3.5" /> Claim Prize
            </button>
          ) : (
            <div
              className="flex-1 px-4 py-3 border border-white/15 text-white/50 text-[11px] uppercase tracking-[0.18em] font-sans flex items-center justify-center gap-2 text-center"
              data-testid="top-seller-claim-locked"
            >
              <Lock className="w-3.5 h-3.5" />
              Claim opens when the month ends
            </div>
          )}
          <Link
            to={`/certificate/${data.year}/${data.month}`}
            onClick={close}
            className="flex-1 px-4 py-3 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.2em] font-sans hover:bg-[#E5C158] transition-colors flex items-center justify-center gap-2"
            data-testid="top-seller-cert-link"
          >
            <Crown className="w-3.5 h-3.5" /> Certificate
          </Link>
        </div>
        <button
          type="button"
          onClick={close}
          className="w-full mt-2 px-4 py-2 text-white/50 hover:text-white text-[10px] uppercase tracking-[0.2em] font-sans"
        >
          Maybe later
        </button>
      </div>
    </div>
  );
}
